/**
 * 前台通知服务
 * 处理用户通知相关的 API 调用
 *
 * @see Requirements 1.1-1.6
 */

import { userApiClient } from "@/lib/userApiClient";

// ============ 类型定义 ============

/**
 * 用户通知
 */
export interface UserNotification {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  broadcast: boolean;
  important: boolean;
  read_at: string | null;
  created_at: string;
}

/**
 * 分页元数据
 */
export interface NotificationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
}

/**
 * 通知列表响应数据
 */
export interface NotificationListData {
  notifications: UserNotification[];
  meta: NotificationMeta;
}

/**
 * 通知列表请求参数
 */
export interface NotificationListParams {
  page?: number;
  per_page?: number;
}

/**
 * 操作响应
 */
export interface NotificationActionResponse {
  success: boolean;
}

// ============ 服务类 ============

/**
 * 通知服务类
 * 提供通知相关的 API 操作
 */
class NotificationService {
  /**
   * 获取通知列表
   * @see Requirements 1.1
   */
  async list(
    params: NotificationListParams = {}
  ): Promise<NotificationListData> {
    const { page = 1, per_page = 8 } = params;

    // 获取当前语言设置
    const locale = localStorage.getItem("i18nextLng") || "zh-CN";

    return userApiClient.get<NotificationListData>("/notifications", {
      params: { page, per_page },
      headers: {
        "X-Locale": locale,
      },
    });
  }

  /**
   * 标记单条通知为已读
   * @see Requirements 1.2
   */
  async markAsRead(id: number): Promise<NotificationActionResponse> {
    return userApiClient.post<NotificationActionResponse>(
      `/notifications/${id}/mark_read`
    );
  }

  /**
   * 标记所有通知为已读
   * @see Requirements 1.3
   */
  async markAllAsRead(): Promise<NotificationActionResponse> {
    return userApiClient.post<NotificationActionResponse>(
      "/notifications/mark_all_read"
    );
  }

  /**
   * 删除通知
   * @see Requirements 1.4
   */
  async delete(id: number): Promise<NotificationActionResponse> {
    return userApiClient.delete<NotificationActionResponse>(
      `/notifications/${id}`
    );
  }
}

// 导出单例
export const notificationService = new NotificationService();
export default notificationService;
